<template>
  <Teleport to="body">
    <Transition name="cm-fade">
      <div v-if="cur" class="fixed inset-0 z-[200] flex items-end justify-center p-0 sm:items-center sm:p-4" @click.self="cancelar">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]"></div>
        <div class="relative w-full max-w-[420px] rounded-t-[24px] bg-card p-5 shadow-2xl sm:rounded-[20px]"
          style="padding-bottom: max(1.25rem, env(safe-area-inset-bottom))" @keydown.esc="cancelar">
          <div class="mx-auto mb-3 h-1.5 w-12 rounded-full bg-border sm:hidden"></div>
          <div class="flex gap-3">
            <span :class="['grid h-11 w-11 shrink-0 place-items-center rounded-2xl', iconWrap]">
              <component :is="iconCmp" class="h-5 w-5" />
            </span>
            <div class="min-w-0 flex-1">
              <h3 v-if="cur.title" class="text-[16px] font-extrabold tracking-tight text-foreground">{{ cur.title }}</h3>
              <p class="text-[13.5px] leading-relaxed text-muted-foreground" :class="cur.title ? 'mt-1' : 'pt-1.5 font-semibold text-foreground'">{{ cur.message }}</p>
            </div>
          </div>

          <input v-if="cur.mode === 'prompt'" ref="inputRef" v-model="valor" :placeholder="cur.placeholder || ''"
            class="mt-3 h-11 w-full rounded-xl border border-border bg-background px-3.5 text-[15px] outline-none focus:border-primary/50"
            @keydown.enter="confirmar" />

          <div class="mt-5 flex gap-2">
            <button v-if="cur.mode !== 'alert'" @click="cancelar"
              class="tap flex-1 rounded-xl bg-secondary py-3 text-sm font-bold text-muted-foreground active:scale-[0.98]">{{ cur.cancelText || 'Cancelar' }}</button>
            <button ref="okRef" @click="confirmar"
              :class="['tap rounded-xl py-3 text-sm font-extrabold text-white active:scale-[0.98]', cur.mode === 'alert' ? 'flex-1' : 'flex-[1.4]', cur.danger ? 'bg-destructive' : 'bg-primary']">{{ cur.confirmText || (cur.mode === 'alert' ? 'Entendi' : 'Confirmar') }}</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
import { AlertTriangle, Trash2, Info, CheckCircle2 } from "lucide-vue-next";
import { onConfirm, type ConfirmRequest } from "@/stores/ui";

const fila = ref<ConfirmRequest[]>([]);
const cur = computed(() => fila.value[0] ?? null);
const valor = ref("");
const inputRef = ref<HTMLInputElement | null>(null);
const okRef = ref<HTMLButtonElement | null>(null);

const iconCmp = computed(() => cur.value?.danger || cur.value?.icon === "danger" ? Trash2
  : cur.value?.icon === "info" ? Info : cur.value?.icon === "ok" ? CheckCircle2 : AlertTriangle);
const iconWrap = computed(() => cur.value?.danger || cur.value?.icon === "danger" ? "bg-destructive/12 text-destructive"
  : cur.value?.icon === "ok" ? "bg-emerald-500/12 text-emerald-600"
  : cur.value?.icon === "info" ? "bg-primary/12 text-primary" : "bg-amber-400/15 text-amber-600");

function abrir(r: ConfirmRequest) {
  valor.value = r.defaultValue ?? "";
  fila.value.push(r);
}
function fechar() { fila.value.shift(); }
function confirmar() {
  const c = cur.value; if (!c) return;
  if (c.mode === "prompt") c.resolve(valor.value.trim() ? valor.value.trim() : null);
  else if (c.mode === "alert") c.resolve(undefined);
  else c.resolve(true);
  fechar();
}
function cancelar() {
  const c = cur.value; if (!c) return;
  if (c.mode === "prompt") c.resolve(null);
  else if (c.mode === "alert") c.resolve(undefined);
  else c.resolve(false);
  fechar();
}
function onKey(e: KeyboardEvent) { if (e.key === "Escape" && cur.value) { e.preventDefault(); cancelar(); } }

watch(cur, (c) => { if (c) nextTick(() => { if (c.mode === "prompt") inputRef.value?.focus(); else okRef.value?.focus(); }); });

let off: (() => void) | null = null;
onMounted(() => { off = onConfirm(abrir); window.addEventListener("keydown", onKey); });
onUnmounted(() => { off?.(); window.removeEventListener("keydown", onKey); });
</script>

<style scoped>
.cm-fade-enter-active, .cm-fade-leave-active { transition: opacity .18s ease; }
.cm-fade-enter-from, .cm-fade-leave-to { opacity: 0; }
</style>
